import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  EditIcon,
  GlobeIcon,
  LayoutGridIcon,
  StoreIcon,
  TrashIcon,
} from "lucide-react";
import { formatPrice } from "@/shared/lib/format-price";
import { RowActions } from "@/shared/ui/RowActions";
import type { FeeRuleDTO } from "../api/client";

const SCOPE_ICONS = {
  global: GlobeIcon,
  shop: StoreIcon,
  category: LayoutGridIcon,
} as const;

function formatValue(row: FeeRuleDTO): string {
  if (row.feeMode === "percent") {
    return `${Number(row.value).toFixed(2)}%`;
  }
  return formatPrice(Number(row.value), "", {
    appendCurrency: false,
    locale: "en-US",
  });
}

type FeeRulesTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: FeeRuleDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onEdit: (row: FeeRuleDTO) => void;
  onDelete: (id: string) => Promise<void>;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

const SCOPE_LABELS: Record<string, string> = {
  global: "ທົ່ວແຜງ",
  shop: "ຮ້ານຄ້າ",
  category: "ຫມວດຫມູ່",
};

export function FeeRulesTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onEdit,
  onDelete,
  onPaginationChange,
  onSortingChange,
}: FeeRulesTableProps) {
  const columns: TanstackReactTable.ColumnDef<FeeRuleDTO>[] = [
    {
      id: "order",
      header: "ລຳດັບ",
      cell: ({ row }) => offset + row.index + 1,
      size: 70,
    },
    createSortableColumn<FeeRuleDTO>("scope", "ຂອບເຂດ", {
      size: 100,
      cell: ({ row }) => {
        const scope = row.original.scope as keyof typeof SCOPE_ICONS;
        const Icon = SCOPE_ICONS[scope] ?? GlobeIcon;
        const label = SCOPE_LABELS[scope] ?? scope;
        return (
          <div className="flex items-center gap-2">
            <Icon className="size-4 text-secondary" aria-hidden />
            <span>{label}</span>
          </div>
        );
      },
    }),
    {
      id: "label",
      header: "ຊື່/ປະເພດ",
      cell: ({ row }) => row.original.label ?? "—",
      size: 180,
    },
    {
      id: "feeMode",
      header: "ແບບ",
      cell: ({ row }) => (
        <Badge
          variant={row.original.feeMode === "percent" ? "secondary" : "success"}
        >
          {row.original.feeMode === "percent" ? "ເປີເຊັນ" : "ຈຳນວນຄົງທີ່"}
        </Badge>
      ),
      size: 100,
    },
    createSortableColumn<FeeRuleDTO>("value", "ຄ່າ", {
      size: 120,
      cell: ({ row }) => formatValue(row.original),
    }),
    {
      id: "currency",
      header: "ສະກຸນເງິນ",
      cell: ({ row }) => row.original.currency ?? "—",
      size: 80,
    },
    createSortableColumn<FeeRuleDTO>("isActive", "ສະຖານະ", {
      size: 100,
      cell: ({ row }) => {
        const active = row.original.isActive;
        return (
          <Badge variant={active ? "success" : "destructive"}>
            {active ? "ໃຊ້ງານ" : "ປິດໃຊ້ງານ"}
          </Badge>
        );
      },
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        if (!canManage) return null;
        const id = row.original.id;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="size-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: "ລຶບ",
            variant: "destructive" as const,
            icon: <TrashIcon className="size-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບກົດຄ່າທຳນຽມ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບກົດນີ້?",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) await onDelete(id);
            },
          },
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 100,
    },
  ];

  return (
    <DataTable<FeeRuleDTO, unknown>
      noDataMessage="ບໍ່ພົບກົດຄ່າທຳນຽມ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting[0]?.id) return;
        onSortingChange(sorting[0].id as string, !!sorting[0].desc);
      }}
    />
  );
}
